import { Mutex } from 'async-mutex';
import { isNil } from '@lucidlink/tscore';
import createWasmModule from '#wasm/assembly/WasmModule';
import { parseWasmResult } from '#wasm/common';
import { ConfigWasm } from './config';
import { KeyValueHelpersWasm } from './key-value-helpers';
import { MetaStoreWasm } from './metastore';
import { registerSharedWasmHandlers, registerSyncWasmHandlers } from './shared-wasm-handlers';
import { TupleWasm } from './tuple';
import { UtilsWasm } from './utils';
export const createServerWasm = async (nodeId, proxy) => {
    const result = new ServerWasmImpl();
    await result.load();
    await result.setUp(nodeId, proxy);
    return result;
};
class ServerWasmImpl {
    modAsync = undefined;
    modSync = undefined;
    metaStoreWasm = undefined;
    utilsWasm = undefined;
    keyValueHelpersWasm = undefined;
    tupleWasm = undefined;
    configWasm = undefined;
    mutex = new Mutex();
    async load() {
        // We load the wasm module twice.
        // The JS async functions are routed through the "async" wasm module.
        // The JS sync functions are routed through the "sync" wasm module.
        // This separation bypasses the Asyncify reentrancy issue
        // when we call a JS sync function and an async function is being executed and has awaited.
        this.modAsync = await createWasmModule();
        this.modSync = await createWasmModule();
        // The registered handlers are used by async functions
        registerSharedWasmHandlers(this.modAsync);
        registerSyncWasmHandlers(this.modSync);
        this.utilsWasm = new UtilsWasm(this.modSync);
        this.tupleWasm = new TupleWasm(this.modSync);
        this.configWasm = new ConfigWasm(this.modSync, this.modAsync, this.mutex);
        this.keyValueHelpersWasm = new KeyValueHelpersWasm(this.modSync);
        this.metaStoreWasm = new MetaStoreWasm(this.modAsync, this.mutex);
    }
    async setUp(nodeId, proxy) {
        this.modSync.SetUpDaemon();
        this.modAsync.SetUpDaemon();
        await this.metaStoreWasm.setUp(nodeId, proxy);
    }
    utils() {
        this.validateWasmLoaded();
        return this.utilsWasm;
    }
    keyValueHelpers() {
        this.validateWasmLoaded();
        return this.keyValueHelpersWasm;
    }
    tuple() {
        this.validateWasmLoaded();
        return this.tupleWasm;
    }
    config() {
        this.validateWasmLoaded();
        return this.configWasm;
    }
    metaStore() {
        this.validateWasmLoaded();
        return this.metaStoreWasm;
    }
    tearDown() {
        parseWasmResult(this.modAsync.CheckForMemoryLeaks());
        parseWasmResult(this.modSync.CheckForMemoryLeaks());
        this.modSync = undefined;
        this.modAsync = undefined;
        this.utilsWasm = undefined;
        this.tupleWasm = undefined;
        this.keyValueHelpersWasm = undefined;
        this.metaStoreWasm = undefined;
    }
    validateWasmLoaded() {
        if (isNil(this.modAsync) || isNil(this.modSync)) {
            throw new Error('Wasm modules are not loaded! Call load() before getting any submodule!');
        }
    }
}
